import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Toast } from 'primereact/toast';
import { Card } from 'primereact/card';
import { Checkbox } from 'primereact/checkbox';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { confirmDialog, ConfirmDialog } from 'primereact/confirmdialog';
import Loading from '../../../Loading/loading';
import { AuthContext } from '../../../Header/auth - context';
import '../settings.css';

/* ══════════════════════════════════════════════════════════════════════════
   Prava mobilne aplikacije — koje kartice ko vidi na telefonu.

   Dva nivoa, isto kao web prava:
     1. podrazumevano po ROLI (za celu firmu)
     2. izuzetak po OSOBI — dodaj ili oduzmi karticu, trajno ili do datuma

   Pravilo računanja je u bazi (TMS_Mobile.auth.menadzerMobilnaPrava) — ovaj ekran
   samo prikazuje i šalje izmene. Kolona „Rezultat" dolazi sa servera, ne računa
   se ovde, da prikaz nikad ne bi rekao nešto drugo od onoga što telefon dobije.
   ══════════════════════════════════════════════════════════════════════════ */

const API = process.env.REACT_APP_BACKEND_URL + '/mobile-operators/prava';

/* 'YYYY-MM-DD' iz lokalnog datuma — rok je dan, ne trenutak; toISOString bi ga
   pomerio na dan ranije posle ponoći. */
const uDan = (d) => {
  if (!d) return null;
  const x = d instanceof Date ? d : new Date(d);
  return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`;
};
/* DATE iz baze stiže kao UTC ponoć — prvih 10 znakova su tačno taj dan. */
const izDana = (v) => (v ? String(v).slice(0, 10) : null);
const prikazDana = (v) => { const d = izDana(v); return d ? d.split('-').reverse().join('.') + '.' : ''; };
const danKaoDate = (v) => { const d = izDana(v); if (!d) return null; const [g, m, dd] = d.split('-').map(Number); return new Date(g, m - 1, dd); };

const MobilnaPravaTab = () => {
  const { t } = useTranslation();
  const auth = useContext(AuthContext);
  const toast = useRef(null);

  const [load, setLoad] = useState(true);
  const [osvezi, setOsvezi] = useState(0);
  const [role, setRole] = useState([]);
  const [matrica, setMatrica] = useState([]);        // [{kljuc, redosled, vozacka, id_rola, pristup, sistemski, izmenjenoUFirmi}]
  const [izmeneRola, setIzmeneRola] = useState({});  // "kljuc|id_rola" -> bool
  const [osobe, setOsobe] = useState([]);
  const [izuzeci, setIzuzeci] = useState([]);

  const [osobaKljuc, setOsobaKljuc] = useState(null); // "TIP:id"
  const [pravaOsobe, setPravaOsobe] = useState([]);
  const [izmeneOsobe, setIzmeneOsobe] = useState({}); // kljuc -> {pristup: 1|0|null, vaziDo, napomena}
  const [ucitavaOsobu, setUcitavaOsobu] = useState(false);
  const [snima, setSnima] = useState(false);

  const poziv = (put, telo) => fetch(API + put, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + auth.token },
    body: JSON.stringify(telo || {}),
  }).then(async (r) => {
    const j = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(j.message || t('admin.m_greska_na_serveru'));
    return j;
  });

  const greska = (e) => toast.current?.show({ severity: 'error', summary: t('mobPrava.greska'), detail: e.message, life: 4000 });
  const uspeh = (poruka) => toast.current?.show({ severity: 'success', summary: poruka, life: 2500 });

  useEffect(() => {
    setLoad(true);
    poziv('/init')
      .then((d) => {
        setRole(d.role || []);
        setMatrica(d.matrica || []);
        setOsobe(d.osobe || []);
        setIzuzeci(d.izuzeci || []);
        setIzmeneRola({});
      })
      .catch(greska)
      .finally(() => setLoad(false));
  }, [osvezi]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── Po roli ───────────────────────────────────────────────────────────── */
  const kartice = useMemo(() => {
    const po = new Map();
    matrica.forEach((m) => { if (!po.has(m.kljuc)) po.set(m.kljuc, { kljuc: m.kljuc, redosled: m.redosled, vozacka: m.vozacka }); });
    return [...po.values()].sort((a, b) => a.redosled - b.redosled);
  }, [matrica]);

  const celija = useMemo(() => new Map(matrica.map((m) => [`${m.kljuc}|${m.id_rola}`, m])), [matrica]);
  const vrednostRole = (kljuc, id_rola) => {
    const k = `${kljuc}|${id_rola}`;
    return k in izmeneRola ? izmeneRola[k] : !!celija.get(k)?.pristup;
  };
  const prebaciRolu = (kljuc, id_rola) => {
    const k = `${kljuc}|${id_rola}`;
    const nova = !vrednostRole(kljuc, id_rola);
    setIzmeneRola((p) => {
      const n = { ...p };
      if (nova === !!celija.get(k)?.pristup) delete n[k]; else n[k] = nova;
      return n;
    });
  };
  const brojIzmenaRola = Object.keys(izmeneRola).length;

  const snimiRole = () => {
    const stavke = Object.entries(izmeneRola).map(([k, v]) => {
      const [kljuc, id_rola] = k.split('|');
      return { kljuc, id_rola: Number(id_rola), pristup: v ? 1 : 0 };
    });
    setSnima(true);
    poziv('/rola', { stavke })
      .then((d) => { uspeh(d.message); setOsvezi((x) => x + 1); if (osobaKljuc) ucitajOsobu(osobaKljuc); })
      .catch(greska)
      .finally(() => setSnima(false));
  };

  const vratiPodrazumevano = () => confirmDialog({
    message: t('mobPrava.potvrdaReset'),
    header: t('mobPrava.vratiPodrazumevano'),
    icon: 'pi pi-exclamation-triangle',
    acceptLabel: t('mobPrava.da'),
    rejectLabel: t('mobPrava.ne'),
    accept: () => {
      setSnima(true);
      poziv('/rolaReset')
        .then((d) => { uspeh(d.message); setOsvezi((x) => x + 1); if (osobaKljuc) ucitajOsobu(osobaKljuc); })
        .catch(greska)
        .finally(() => setSnima(false));
    },
  });

  /* ── Po osobi ──────────────────────────────────────────────────────────── */
  const nazivRole = (id) => role.find((r) => r.id === id)?.naziv || '—';
  const opcijeOsoba = osobe.map((o) => ({
    value: `${o.tip}:${o.id}`,
    label: `${o.ime} — ${nazivRole(o.id_rola)} · ${o.tip === 'MOBILNI' ? t('mobPrava.mobilniNalog') : t('mobPrava.operater')}${o.status ? '' : ` (${t('mobPrava.blokiran')})`}`,
  }));

  const ucitajOsobu = (kljuc) => {
    if (!kljuc) { setPravaOsobe([]); return; }
    const [tip, id] = kljuc.split(':');
    setUcitavaOsobu(true);
    setIzmeneOsobe({});
    poziv('/osoba', { tip, id: Number(id) })
      .then((d) => setPravaOsobe(d.prava || []))
      .catch(greska)
      .finally(() => setUcitavaOsobu(false));
  };

  const izaberiOsobu = (kljuc) => { setOsobaKljuc(kljuc); ucitajOsobu(kljuc); };

  /* Trenutna vrednost reda: izmena ako postoji, inače ono što je u bazi.
     Istekao izuzetak se prikazuje kao „po roli" — više ne važi. */
  const stanjeReda = (p) => {
    if (p.kljuc in izmeneOsobe) return izmeneOsobe[p.kljuc];
    const vazi = p.osobaPristup !== null && p.osobaPristup !== undefined && !p.osobaIstekla;
    return { pristup: vazi ? (p.osobaPristup ? 1 : 0) : null, vaziDo: vazi ? izDana(p.vaziDo) : null, napomena: vazi ? (p.napomena || '') : '' };
  };
  const izmeniRed = (p, polje, vrednost) => setIzmeneOsobe((s) => {
    const novo = { ...stanjeReda(p), [polje]: vrednost };
    if (polje === 'pristup' && vrednost === null) { novo.vaziDo = null; novo.napomena = ''; }
    return { ...s, [p.kljuc]: novo };
  });
  const brojIzmenaOsobe = Object.keys(izmeneOsobe).length;

  const snimiOsobu = () => {
    const [tip, id] = osobaKljuc.split(':');
    const stavke = Object.entries(izmeneOsobe).map(([kljuc, v]) => ({
      kljuc, pristup: v.pristup, vaziDo: v.pristup === null ? null : v.vaziDo, napomena: v.pristup === null ? null : (v.napomena || null),
    }));
    setSnima(true);
    poziv('/osobaSnimi', { tip, id: Number(id), stavke })
      .then((d) => { uspeh(d.message); ucitajOsobu(osobaKljuc); setOsvezi((x) => x + 1); })
      .catch(greska)
      .finally(() => setSnima(false));
  };

  /* Rezultat za prikaz: posle snimanja dolazi sa servera; dok ima nesnimljenih
     izmena, prikazuje se šta ĆE biti (izuzetak, inače ono što rola daje). */
  const rezultatReda = (p) => {
    if (!(p.kljuc in izmeneOsobe)) return !!p.pristup;
    const s = izmeneOsobe[p.kljuc];
    if (s.pristup !== null) return s.pristup === 1;
    return !!p.rolaPristup || !!p.vozacPristup;
  };

  const opcijeIzuzetka = [
    { value: null, label: t('mobPrava.poRoliOpcija') },
    { value: 1, label: t('mobPrava.dozvoli') },
    { value: 0, label: t('mobPrava.zabrani') },
  ];

  const imeOsobe = (tip, id) => osobe.find((o) => o.tip === tip && o.id === id)?.ime || `${tip} ${id}`;
  const nazivKartice = (kljuc) => t(`mobPrava.modul.${kljuc}`);

  return (
    <div id="setting_main">
      {load && <Loading />}
      <Toast ref={toast} />
      <ConfirmDialog />

      <div className="setting_table mob-prava">
        {/* ── 1. Po roli ── */}
        <Card>
          <div className="mob-prava-zaglavlje">
            <div>
              <div className="perm-title">{t('mobPrava.naslovRola')}</div>
              <p className="perm-note">{t('mobPrava.opisRola')}</p>
            </div>
            <div className="mob-prava-dugmad">
              <Button type="button" label={t('mobPrava.vratiPodrazumevano')} icon="pi pi-undo"
                      severity="secondary" outlined size="small" onClick={vratiPodrazumevano} disabled={snima} />
              <button type="button" className="btn btn-success" onClick={snimiRole} disabled={snima || !brojIzmenaRola}>
                {t('mobPrava.sacuvaj')}{brojIzmenaRola ? ` (${brojIzmenaRola})` : ''}
              </button>
            </div>
          </div>
          <div className="mob-prava-skrol">
            <table className="perm-table mob-prava-tabela">
              <thead>
                <tr>
                  <th className="mob-prava-kartica">{t('mobPrava.kartica')}</th>
                  {role.map((r) => <th key={r.id} className="perm-col">{r.naziv}</th>)}
                </tr>
              </thead>
              <tbody>
                {kartice.map((k) => (
                  <tr key={k.kljuc}>
                    <td className="mob-prava-kartica">
                      {nazivKartice(k.kljuc)}
                      {!!k.vozacka && <span className="mob-prava-oznaka" title={t('mobPrava.vozackaNapomena')}>{t('mobPrava.vozacka')}</span>}
                    </td>
                    {role.map((r) => {
                      const c = celija.get(`${k.kljuc}|${r.id}`);
                      const k2 = `${k.kljuc}|${r.id}`;
                      const promenjeno = k2 in izmeneRola;
                      return (
                        <td key={r.id} className={`perm-col${promenjeno ? ' mob-prava-nesnimljeno' : ''}`}>
                          <Checkbox checked={vrednostRole(k.kljuc, r.id)} onChange={() => prebaciRolu(k.kljuc, r.id)} />
                          {!promenjeno && c?.izmenjenoUFirmi && (
                            <span className="mob-prava-tacka" title={t('mobPrava.izmenjenoUFirmi')} />
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="perm-note mob-prava-legenda">
            <span className="mob-prava-tacka" /> {t('mobPrava.izmenjenoUFirmi')} · {t('mobPrava.vozackaNapomena')}
          </p>
        </Card>

        {/* ── 2. Po osobi ── */}
        <Card>
          <div className="mob-prava-zaglavlje">
            <div>
              <div className="perm-title">{t('mobPrava.naslovOsoba')}</div>
              <p className="perm-note">{t('mobPrava.opisOsoba')}</p>
            </div>
            {!!osobaKljuc && (
              <div className="mob-prava-dugmad">
                <button type="button" className="btn btn-success" onClick={snimiOsobu} disabled={snima || !brojIzmenaOsobe}>
                  {t('mobPrava.sacuvaj')}{brojIzmenaOsobe ? ` (${brojIzmenaOsobe})` : ''}
                </button>
              </div>
            )}
          </div>

          <Dropdown
            value={osobaKljuc}
            options={opcijeOsoba}
            onChange={(e) => izaberiOsobu(e.value)}
            placeholder={t('mobPrava.izaberiOsobu')}
            filter
            showClear
            className="mob-prava-osoba"
            emptyMessage={t('mobPrava.nemaOsoba')}
          />

          {ucitavaOsobu && <div className="perm-loading">{t('admin.ucitavamPrava')}</div>}

          {!!osobaKljuc && !ucitavaOsobu && pravaOsobe.length > 0 && (
            <div className="mob-prava-skrol">
              <table className="perm-table mob-prava-tabela">
                <thead>
                  <tr>
                    <th className="mob-prava-kartica">{t('mobPrava.kartica')}</th>
                    <th>{t('mobPrava.poRoli')}</th>
                    <th>{t('mobPrava.izuzetak')}</th>
                    <th>{t('mobPrava.vaziDo')}</th>
                    <th>{t('mobPrava.napomena')}</th>
                    <th>{t('mobPrava.rezultat')}</th>
                  </tr>
                </thead>
                <tbody>
                  {pravaOsobe.map((p) => {
                    const s = stanjeReda(p);
                    const rez = rezultatReda(p);
                    return (
                      <tr key={p.kljuc} className={p.kljuc in izmeneOsobe ? 'mob-prava-nesnimljeno' : ''}>
                        <td className="mob-prava-kartica">{nazivKartice(p.kljuc)}</td>
                        <td>
                          {p.rolaPristup || p.vozacPristup
                            ? <span className="mob-prava-da">✓{!p.rolaPristup && p.vozacPristup ? ` ${t('mobPrava.kaoVozac')}` : ''}</span>
                            : <span className="mob-prava-ne">—</span>}
                        </td>
                        <td>
                          <Dropdown value={s.pristup} options={opcijeIzuzetka} onChange={(e) => izmeniRed(p, 'pristup', e.value ?? null)}
                                    placeholder={t('mobPrava.poRoliOpcija')}
                                    className="mob-prava-izbor" />
                          {p.osobaIstekla && !(p.kljuc in izmeneOsobe) && (
                            <div className="mob-prava-isteklo">{t('mobPrava.isteklo', { datum: prikazDana(p.vaziDo) })}</div>
                          )}
                        </td>
                        <td>
                          <Calendar
                            value={danKaoDate(s.vaziDo)}
                            onChange={(e) => izmeniRed(p, 'vaziDo', uDan(e.value))}
                            dateFormat="dd.mm.yy."
                            minDate={new Date()}
                            showButtonBar
                            placeholder={t('mobPrava.trajno')}
                            disabled={s.pristup === null}
                            className="mob-prava-datum"
                          />
                        </td>
                        <td>
                          <InputText value={s.napomena || ''} maxLength={200}
                                     onChange={(e) => izmeniRed(p, 'napomena', e.target.value)}
                                     disabled={s.pristup === null} placeholder={t('mobPrava.napomenaPh')}
                                     className="mob-prava-napomena" />
                        </td>
                        <td>
                          <span className={`mob-prava-rezultat ${rez ? 'da' : 'ne'}`}>
                            {rez ? t('mobPrava.vidi') : t('mobPrava.neVidi')}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {/* ── 3. Pregled svih izuzetaka ── */}
        <Card>
          <div className="perm-title">{t('mobPrava.aktivniIzuzeci')}</div>
          {izuzeci.length === 0 ? (
            <p className="perm-note">{t('mobPrava.nemaIzuzetaka')}</p>
          ) : (
            <div className="mob-prava-skrol">
              <table className="perm-table mob-prava-tabela">
                <thead>
                  <tr>
                    <th className="mob-prava-kartica">{t('mobPrava.osoba')}</th>
                    <th>{t('mobPrava.kartica')}</th>
                    <th>{t('mobPrava.izuzetak')}</th>
                    <th>{t('mobPrava.vaziDo')}</th>
                    <th>{t('mobPrava.napomena')}</th>
                  </tr>
                </thead>
                <tbody>
                  {izuzeci.map((z) => (
                    <tr key={`${z.tip_korisnika}:${z.id_korisnika}:${z.kljuc}`}
                        className={`mob-prava-klik${z.istekao ? ' mob-prava-istekao' : ''}`}
                        onClick={() => izaberiOsobu(`${z.tip_korisnika}:${z.id_korisnika}`)}>
                      <td className="mob-prava-kartica">{imeOsobe(z.tip_korisnika, z.id_korisnika)}</td>
                      <td>{nazivKartice(z.kljuc)}</td>
                      <td>{z.pristup ? <span className="mob-prava-da">{t('mobPrava.dodato')}</span> : <span className="mob-prava-oduzeto">{t('mobPrava.oduzeto')}</span>}</td>
                      <td>{z.vaziDo ? (z.istekao ? t('mobPrava.isteklo', { datum: prikazDana(z.vaziDo) }) : prikazDana(z.vaziDo)) : t('mobPrava.trajno')}</td>
                      <td>{z.napomena || ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default MobilnaPravaTab;
